import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useFeature } from "../../../common/FeatureFlags.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { OrganisationDetailsAboutBox } from "../../features/organisations/boxes/OrganisationDetailsAboutBox.js";
import { OrganisationDetailsAddressesBox } from "../../features/organisations/boxes/OrganisationDetailsAddressesBox.js";
import { OrganisationDetailsDataBox } from "../../features/organisations/boxes/OrganisationDetailsDataBox.js";
import { OrganisationDetailsOpeningHoursBox } from "../../features/organisations/boxes/OrganisationDetailsOpeningHoursBox.js";
import { OrganisationDetailsProfilePicturesBox } from "../../features/organisations/boxes/OrganisationDetailsProfilePicturesBox.js";
import { OrganisationDetailsQualificationsBox } from "../../features/organisations/boxes/OrganisationDetailsQualificationsBox.js";
import { OrganisationDetailsServicesBox } from "../../features/organisations/boxes/OrganisationDetailsServicesBox.js";
import { OrganisationRegistrationDetailsBox } from "../../features/organisations/boxes/OrganisationRegistrationDetailsBox.js";
import { useOrganisationPublicProfileQuery } from "../../public-routes/OrganisationPublicProfile.generated.js";
import { useOrganisationDetailsQuery } from "./OrganisationDetails.generated.js";

interface Props {
  submenuItems: PageTopbarItem[];
}

export function OrganisationDetails({ submenuItems }: Props) {
  const { t } = useTranslate(["OrganisationPageTopbar"]);
  const debug = useFeature("DebugOutput");

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("Organisation", { ns: "OrganisationPageTopbar" }),
      to: "/organisation",
    },
    {
      title: t("Organisation Details", { ns: "OrganisationPageTopbar" }),
      to: "/organisation/profile",
    },
  ];

  const client = useApolloClient();
  const query = useOrganisationDetailsQuery({
    client,
  });

  const organisation = getDataOrNull(query.data?.viewer)?.organisation;
  const profileSlug = organisation?.profile?.slug;

  const publicProfileQuery = useOrganisationPublicProfileQuery({
    client,
    skip: !profileSlug,
    variables: { slug: profileSlug || "" },
  });

  const profile = getDataOrNull(
    publicProfileQuery.data?.organisationProfile
  )?.profile;

  const isCraftsman = organisation?.organisationType === "CRAFTSMAN";

  return (
    <Page
      title={t("Organisation Details", { ns: "OrganisationPageTopbar" })}
      submenuItems={submenuItems}
      breadcrumbs={breadcrumbs}
      breadcrumbsSize="medium"
    >
      {organisation && (
        <PageContainer>
          <Stack
            spacing={2}
            sx={{
              width: "100%",
              maxWidth: 640,
            }}
          >
            <OrganisationDetailsProfilePicturesBox
              organisation={organisation}
              isViewerOrganisation={true}
            />

            <OrganisationDetailsDataBox
              organisation={organisation}
              isViewerOrganisation={true}
              debug={debug}
            />

            <OrganisationDetailsAddressesBox
              organisation={organisation}
              isViewerOrganisation={true}
            />

            <OrganisationDetailsAboutBox
              organisation={organisation}
              isViewerOrganisation={true}
            />

            {isCraftsman && (
              <>
                <OrganisationDetailsQualificationsBox
                  organisation={organisation}
                />
                <OrganisationDetailsServicesBox
                  organisation={organisation}
                  isViewerOrganisation={true}
                />
                {/* {profile && (
                  <OrganisationDetailsOpeningHoursBox
                    organisationId={organisation.id}
                    profile={profile}
                  />
                )} */}
                {/* TODO: reintroduce later when the mxcall<>ms-platform migration is done */}
                <OrganisationRegistrationDetailsBox
                  organisation={organisation}
                />
              </>
            )}
          </Stack>
        </PageContainer>
      )}
    </Page>
  );
}
